import { Toast } from '../../../../components/Common/Toast';
import { callAPI } from '../../../../services/api.service';
import { BookGenerationRow, BookGenerationStatus } from '../../../../interfaces/bookGeneration.interface';

export interface BookGenerationsFilters {
  status?: BookGenerationStatus;
  dateFrom?: string;
  dateTo?: string;
  email?: string;
  hasErrorsOnly?: boolean;
}

export const fetchBookGenerations = async (
  pageNumber: number,
  filters: BookGenerationsFilters = {},
  search?: string,
): Promise<{ books: BookGenerationRow[]; totalPages: number; totalRecords: number }> => {
  try {
    const params = new URLSearchParams();
    params.set('pageNumber', String(pageNumber));
    params.set('pageSize', '30');
    if (filters.status) params.set('status', filters.status);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.email) params.set('email', filters.email);
    if (filters.hasErrorsOnly) params.set('hasErrorsOnly', 'true');
    if (search) params.set('search', search);

    const response = await callAPI(`admin/book-generations?${params.toString()}`);
    return {
      books: response?.books || [],
      totalPages: response?.totalPages || 0,
      totalRecords: response?.totalRecords || 0,
    };
  } catch (error) {
    Toast('Something went wrong fetching book generations', true);
    throw error;
  }
};

export const searchBookGenerations = async (
  searchTerm: string,
): Promise<BookGenerationRow[]> => {
  try {
    const response = await callAPI(
      `admin/book-generations/search?searchTerm=${encodeURIComponent(searchTerm)}`,
    );
    return response?.books || [];
  } catch (error) {
    Toast('Error searching book generations', true);
    throw error;
  }
};

export interface QueueTimeStats {
  avgQueueTimeMs: number;
  maxQueueTimeMs: number;
  sampleCount: number;
  message: string;
}

export const fetchQueueTimeStats = async (hoursBack: number = 24): Promise<QueueTimeStats> => {
  try {
    const response = await callAPI(`admin/book-generations/queue-stats?hoursBack=${hoursBack}`);
    return {
      avgQueueTimeMs: response?.avgQueueTimeMs || 0,
      maxQueueTimeMs: response?.maxQueueTimeMs || 0,
      sampleCount: response?.sampleCount || 0,
      message: response?.message || '',
    };
  } catch (error) {
    Toast('Error fetching queue stats', true);
    return { avgQueueTimeMs: 0, maxQueueTimeMs: 0, sampleCount: 0, message: 'Error' };
  }
};

export const updateBookPageTotal = async (bookId: string, pageTotal: number): Promise<void> => {
  try {
    await callAPI(`admin/book-generations/${bookId}`, { pageTotal }, 'PATCH');
    Toast('Page total updated', false);
  } catch (error) {
    Toast('Error updating page total', true);
    throw error;
  }
};
